import { RouterModule, Routes, UrlSegment } from '@angular/router';

// --
import {AuthGuard, UrlPatternGuard} from '@app/core';
import {InvoiceCategoriesModule} from "@app/features/system-settings/invoice-categories/invoice-categories.module";
import {AddJobTemplateModule} from "@app/features/customers/jobs/add-job-template/add-job-template.module";
import {ContractTermsAndConditionsModule} from "@app/features/system-settings/contract-terms-and-conditions/contract-terms-and-conditions.module";
import {AdvancedBookingFormModule} from "@app/features/system-settings/advanced-booking-form/advanced-booking-form.module";
import {LicenseContractModule} from "@app/features/company-settings/license-contract/license-contract.module";
import {NominalAccountsModule} from "@app/features/system-settings/nominal-accounts/nominal-accounts.module";
import {TimesheetSetupModule} from "@app/features/company-settings/timesheet-setup/timesheet-setup.module";
// import {RegionalSettingsModule} from "@app/features/company-settings/regional-settings/regional-settings.module";
import {CustomerModule} from '@app/features/customers/customer/customer.module';
import {CustomerVatModule} from '@app/features/system-settings/customer-vat/customer-vat.module.ts';
import {JobReportCustomisationModule} from "@app/features/system-settings/job-report-customisation/job-report-customisation.module";
import { AiDocumentParsingModule } from '@app/features/ai-document-parsing/ai-document-parsing.module';
import { SetUpStagesModule } from "@app/features/system-settings/set-up-stages/set-up-stages.module";
import { LabourTimeApprovalModule } from "@app/features/system-settings/labour-time-approval/labour-time-approval.module";
import { VariationOptionModule } from "@app/features/system-settings/variations/variation-option/variation-option.module";
import { AidenModule } from "@app/features/aiden/aiden.module";

export function isAngular18Url(url: UrlSegment[]) {
    return url.length > 0 && url[0].path.startsWith('reporting_dashboard_v2') ? ({ consumed: url }) :
        url[0].path.startsWith('reporting_dashboard_new') ? ({ consumed: url }) : null;
}
export const FeaturesRoutes: Routes = [
    {
        matcher: isAngular18Url,
        loadChildren: '@app/features/reporting-mf/reporting-mf.module#ReportingMfModule',
        canActivate: [AuthGuard],
    },
    {
        path: 'system_settings/contract-templates',
        loadChildren: '@app/features/system-settings/contract-templates/contract-templates.module#ContractTemplatesModule',
        canActivate: [AuthGuard],
    },
    {
        path: 'system_settings/contract_templates',
        loadChildren: '@app/features/system-settings/contracts/contract-templates/contract-template-screen.module#ContractTemplateScreenModule',
    },
    {
        path: 'system_settings/sla_template',
        loadChildren: '@app/features/system-settings/sla-setup/sla-setup.module#SlaSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/ppm',
        loadChildren: '@app/features/system-settings/ppm-setup/ppm-setup.module#PpmSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/:type/:id/contracts',
        loadChildren: '@app/features/customers/contracts/contracts.module#ContractsModule',
        canActivate: [AuthGuard, UrlPatternGuard],
        data: {
            urlPatterns: {type: 'customer|customer_list|work_address', id: '[0-9]+'}
        }
    },
    {
        path: 'customers/:type/:id/customer_contracts',
        loadChildren: '@app/features/customers/customer-contracts/customer-contracts.module#CustomerContractsModule',
        canActivate: [AuthGuard, UrlPatternGuard],
        data: {
            urlPatterns: {type: 'customer|customer_list|work_address', id: '[0-9]+'}
        }
    },
    {
        path: 'system_settings/ppm-jobs-setup',
        loadChildren: '@app/features/system-settings/ppm-jobs-setup/ppm-jobs-setup.module#PpmJobsSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/contract-categories',
        loadChildren: '@app/features/system-settings/contract-categories/contract-categories.module#ContractCategoriesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/sla_email_template',
        loadChildren: '@app/features/system-settings/common-template-list-view/common-template-list-view.module#CommonTemplateListViewModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'notifications',
        loadChildren: '@app/features/notifications/notifications.module#NotificationsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/custom-forms',
        redirectTo: 'settings/custom_forms',
        // loadChildren: '@app/features/system-settings/dynamic-certificates/dynamic-certificates.module#DynamicCertificatesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/custom_forms_categories',
        loadChildren: '@app/features/system-settings/custom-form/custom-form-categories/custom-form-categories.module#CustomFormCategoriesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/invoice_message',
        loadChildren: '@app/features/system-settings/invoice-messages/invoice-messages.module#InvoiceMessagesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/customise_invoice_names',
        loadChildren: '@app/features/system-settings/customise-invoice-names/customise-invoice-names.module#CustomiseInvoiceNamesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/:customerType/:customerId/multiple_invoice',
        loadChildren: '@app/features/customers/invoices/customer-multiple-invoices.module#CustomerMultipleInvoicesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/:type/:customerId/jobs/:jobId/invoices',
        loadChildren: '@app/features/customers/jobs/create-edit-invoice/create-edit-invoice.module#CreateEditInvoiceModule',
        canActivate: [AuthGuard, UrlPatternGuard],
        data: {
            urlPatterns: {type: 'customer|work_address', customerId: '[0-9]+', jobId: '[0-9]+'}
        }
    },
    {
        path: 'system_settings/parts_and_prices',
        loadChildren: '@app/features/system-settings/parts-and-prices/parts-and-prices.module#PartsAndPricesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/stock_locations',
        loadChildren: '@app/features/system-settings/stock-locations/stock-locations.module#StockLocationsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/stock_inventory_system',
        loadChildren: '@app/features/system-settings/stock-inventory-system/stock-inventory-system.module#StockInventorySystemModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'invoice_address/:invoiceAddressId/multiple_invoice',
        loadChildren: '@app/features/customers/invoices/invoice-address-multiple-invoices.module#InvoiceAddressMultipleInvoicesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/labour_rates',
        loadChildren: '@app/features/system-settings/labour-rates/labour-rates.module#LabourRatesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/contract-renewal-setup',
        loadChildren: '@app/features/system-settings/contract-renewal-setup/contract-renewal-setup.module#ContractRenewalSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'company_settings/license_contract',
        loadChildren: '@app/features/company-settings/license-contract/license-contract.module#LicenseContractModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/price_books',
        loadChildren: '@app/features/system-settings/price-books/price-books.module#PriceBooksModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/advanced_booking_form',
        loadChildren: '@app/features/system-settings/advanced-booking-form/advanced-booking-form.module#AdvancedBookingFormModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/job-descriptions',
        loadChildren: '@app/features/system-settings/job-descriptions/job-descriptions-view.module#JobDescriptionsViewModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/invoicecategories',
        loadChildren: '@app/features/system-settings/invoice-categories/invoice-categories.module#InvoiceCategoriesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/:type/:id/jobs',
        loadChildren: '@app/features/customers/jobs/add-job-template/add-job-template.module#AddJobTemplateModule',
        canActivate: [AuthGuard, UrlPatternGuard],
        data: {
            urlPatterns: {type: 'customer|customer_list|work_address', id: '[0-9]+'}
        }
    },
    {
        path: 'system_settings/pricing_items',
        loadChildren: '@app/features/system-settings/pricing-items/pricing-items.module#PricingItemsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/tracking_for_photos',
        loadChildren: '@app/features/system-settings/tracking-for-photos/tracking-for-photos.module#TrackingForPhotosModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/tags',
        loadChildren: '@app/features/system-settings/file-tags/tags.module#TagsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/terms_and_conditions_contract',
        loadChildren: '@app/features/system-settings/contract-terms-and-conditions/contract-terms-and-conditions.module#ContractTermsAndConditionsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/contract-renewal-setup',
        loadChildren: '@app/features/system-settings/contract-renewal-setup/contract-renewal-setup.module#ContractRenewalSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'company_settings/uk_industry_specific_number',
        loadChildren: '@app/features/company-settings/uk-specific/uk-specific.module#UkSpecificModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/scheduling-options',
        loadChildren: '@app/features/system-settings/scheduling-options/scheduling-options.module#SchedulingOptionsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/leave-job-questions',
        redirectTo: 'settings/jobs/leave_questions',
        // loadChildren: '@app/features/system-settings/leave-job-questions/leave-job-questions.module#LeaveJobQuestionsModule',
        canActivate: [AuthGuard]
    },

    {
        path: 'system_settings/arrive-questions',
        redirectTo: 'settings/jobs/arrive_questions',
        // loadChildren: '@app/features/system-settings/arrive-questions/arrive-questions.module#ArriveQuestionsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'company_settings/timesheet_setup',
        loadChildren: '@app/features/company-settings/timesheet-setup/timesheet-setup.module#TimesheetSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/customer_portal_email_message',
        loadChildren: '@app/features/system-settings/customer-portal/customer-portal.module#CustomerPortalModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/nominal-accounts',
        loadChildren: '@app/features/system-settings/nominal-accounts/nominal-accounts.module#NominalAccountsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'company_settings/integrations',
        loadChildren: '@app/features/company-settings/integrations/integrations.module#IntegrationsModule',
        canActivate: [AuthGuard]
    },
    // {
    //     path: 'company_settings/regional_settings',
    //     loadChildren: '@app/features/company-settings/regional-settings/regional-settings.module#RegionalSettingsModule',
    //     canActivate: [AuthGuard]
    // },
    {
        path: 'invoice_address',
        loadChildren: '@app/features/customers/customer/customer.module#CustomerModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/call-custom-fields',
        loadChildren: '@app/features/system-settings/call-custom-fields/call-custom-fields.module#CallCustomFieldsModule'
    },
    {
        path: 'system_settings/engineer_portal_settings',
        loadChildren: '@app/features/system-settings/engineer-portal/engineer-portal.module#EngineerPortalModule'
    },
    {
        path: 'system_settings/after_sales_portal_settings',
        loadChildren: '@app/features/system-settings/after-sales-portal/after-sales-portal.module#AfterSalesPortalModule'
    },
    {
        path: 'system_settings/statement_portal_settings',
        loadChildren: '@app/features/system-settings/statement-portal/statement-portal.module#StatementPortalModule'
    },
    {
        path: 'system_settings/invoice_portal_settings',
        loadChildren: '@app/features/system-settings/invoice-portal/invoice-portal.module#InvoicePortalModule'
    },
    {
        path: 'system_settings/payment_portal_settings',
        loadChildren: '@app/features/system-settings/payment-portal/payment-portal.module#PaymentPortalModule'
    },
    {
        path: 'system_settings/appointment_booking_portal_settings',
        loadChildren: '@app/features/system-settings/job-booking-portal/job-booking-portal.module#JobBookingPortalModule'
    },
    {
        path: 'system_settings/service_reminder_portal_settings',
        loadChildren: '@app/features/system-settings/service-reminder-portal/service-reminder-portal.module#ServiceReminderPortalModule'
    },
    {
        path: 'system_settings/rebooking_portal_settings',
        loadChildren: '@app/features/system-settings/rebooking-portal/rebooking-portal.module#RebookingPortalModule'
    },
    {
        path: 'system_settings/service_scheduling_rules_settings',
        loadChildren: '@app/features/system-settings/service-scheduling-rules-portal/service-scheduling-rules-portal.module#ServiceSchedulingRulesPortalModule'
    },
    {
        path: 'system_settings/set_up_pipelines',
        loadChildren: '@app/features/system-settings/set-up-pipelines/set-up-pipelines.module#SetUpPipelinesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/opportunity_template',
        loadChildren: '@app/features/system-settings/opportunity-template/opportunity-template.module#OpportunityTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/proposal_templates',
        loadChildren: '@app/features/system-settings/proposal-template/proposal-template.module#ProposalTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_types',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-types/vehicle-types.module#VehicleTypesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_models',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-models/vehicle-model.module#VehicleModelModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/option_template',
        loadChildren: '@app/features/system-settings/option-template/option-template.module#OptionTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/call_note_templates',
        loadChildren: '@app/features/system-settings/call-note-template/call-note-template.module#CallNoteTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/general-custom-templates',
        loadChildren: '@app/features/system-settings/general-custom-templates/general-custom-templates.module#GeneralCustomTemplatesModule'
    },
    {
        path: 'system_settings/general-custom-templates-config',
        loadChildren: '@app/features/system-settings/general-custom-templates-config/general-custom-templates-config.module#GeneralCustomTemplatesConfigModule'
    },
    {
        path: 'system_settings/reporting_categories',
        loadChildren: '@app/features/system-settings/reporting-category/reporting-category.module#ReportingCategoryModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/customer/:customerId/opportunity',
        loadChildren: '@app/features/customers/add-opportunity/add-opportunity.module#AddOpportunityModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/customer_list/:customerId/jobs/:jobId/opportunity/new',
        loadChildren: '@app/features/system-settings/job-opportunity/job-opportunity.module#JobOpportunityModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'scheduled_activities',
        loadChildren: '@app/features/scheduled-activities/scheduled-activities.module#ScheduledActivitiesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'opportunities',
        loadChildren: '@app/features/opportunities/opportunities.module#OpportunitiesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'defects',
        loadChildren: '@app/features/customers/defects/defects.module#DefectsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/opportunity_reject_reasons',
        loadChildren: '@app/features/system-settings/settings-reasons/settings-reasons.module#SettingsReasonsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/opportunity_cancel_reasons',
        loadChildren: '@app/features/system-settings/settings-reasons/settings-reasons.module#SettingsReasonsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/customer_view_template',
        loadChildren: '@app/features/customers/customer/customer.module#CustomerModule'
    },
    {
        path: 'customers',
        loadChildren: '@app/features/customers/customer/customer.module#CustomerModule',
        canActivate: [AuthGuard],
    },
    {
        path: 'system_settings/acceptance_template',
        loadChildren: '@app/features/system-settings/opportunity-proposal-confirmation/opportunity-proposal-confirmation.module#OpportunityProposalConfirmationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/finance_templates',
        loadChildren: '@app/features/system-settings/finance-templates/finance-templates.module#FinanceTemplatesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/grant_templates',
        loadChildren: '@app/features/system-settings/grants-templates/grants-templates.module#GrantsTemplatesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/finance_communication',
        loadChildren: '@app/features/system-settings/finance-communication/finance-communication.module#FinanceCommunicationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/branding_colors',
        loadChildren: '@app/features/system-settings/branding-colors/branding-colors.module#BrandingColorsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/proposal_branding',
        loadChildren: '@app/features/system-settings/proposal-branding/proposal-branding.module#ProposalBrandingModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/opportunity_engineer_message',
        loadChildren: '@app/features/system-settings/opportunity-engineer-message/opportunity-engineer-message.module#OpportunityEngineerMessageModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/opportunity_arrive_questions',
        loadChildren: '@app/features/system-settings/arrive-questions/arrive-questions.module#ArriveQuestionsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/opportunity_leave_questions',
        loadChildren: '@app/features/system-settings/leave-job-questions/leave-job-questions.module#LeaveJobQuestionsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/firstopportunitynumber',
        loadChildren: '@app/features/system-settings/opportunity-number/opportunity-number.module#OpportunityNumberModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings',
        loadChildren: '@app/features/settings/settings.module#SettingsModule'
    },
    {
        path: 'system_settings/workflow_marketplace',
        loadChildren: '@app/features/system-settings/workflow-marketplace/workflow-marketplace.module#WorkflowMarketplaceModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/workflow_setup',
        loadChildren: '@app/features/system-settings/workflow-setup/workflow-setup.module#WorkflowSetupModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/workflow_setup_sequence',
        loadChildren: '@app/features/system-settings/workflow-setup-sequence/workflow-setup-sequence.module#WorkflowSetupSequenceModule',
        canActivate: [AuthGuard]
    },
    {
        path:'system_settings/email_templates',
        loadChildren:'@app/features/system-settings/settings-email-templates/settings-email-templates.module#SettingsEmailTemplatesModule',
        canActivate: [AuthGuard]
    },
    {
        path:'system_settings/sms_templates',
        loadChildren:'@app/features/system-settings/settings-sms-templates/settings-sms-templates.module#SettingsSmsTemplatesModule',
        canActivate: [AuthGuard]
    },
    {
        path:'system_settings/letter_templates',
        loadChildren:'@app/features/system-settings/settings-letter-template/settings-letter-template.module#SettingsLetterTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'customers/:type/:customerId/opportunities',
        loadChildren: '@app/features/customers/jobs/create-edit-invoice/create-edit-invoice.module#CreateEditInvoiceModule',
        canActivate: [AuthGuard, UrlPatternGuard],
        data: {
            urlPatterns: {type: 'customer|work_address', customerId: '[0-9]+', opportunityId: '[0-9]+'}
        }
    },
    {
        path: 'jobs',
        loadChildren: '@app/features/jobs-tab/jobs-tab.module#JobsTabModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/saved_colors',
        loadChildren: '@app/features/system-settings/saved-colors/saved-colors.module#SavedColorsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/addon_template',
        loadChildren: '@app/features/system-settings/option-template/option-template.module#OptionTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/bolt_on_templates',
        loadChildren: '@app/features/system-settings/bolt-on-templates/bolt-on-templates.module#BoltOnTemplatesModule'
    },
    {
        path: 'system_settings/contracts_ppm',
        loadChildren: '@app/features/system-settings/contracts/contract-ppm/contract-ppm-setup.module#ContractPpmSetupModule'
    },
    {
        path: 'system_settings/contracts_category',
        loadChildren: '@app/features/system-settings/contracts/contract-categories/contract-categories-setup.module#ContractCategoriesSetupModule'
    },
    {
        path: 'system_settings/customer_vat',
        loadChildren: '@app/features/system-settings/customer-vat/customer-vat.module#CustomerVatModule'
    },
    {
        path: 'system_settings/loan_application_submitted',
        loadChildren: '@app/features/system-settings/loan-application-template/loan-application.module#LoanApplicationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/loan_application_accepted',
        loadChildren: '@app/features/system-settings/loan-application-template/loan-application.module#LoanApplicationModule',
        canActivate: [AuthGuard],
    },
    {
        path: 'system_settings/loan_application_rejected',
        loadChildren: '@app/features/system-settings/loan-application-template/loan-application.module#LoanApplicationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/supplier_quotation_email_template',
        loadChildren: '@app/features/system-settings/opportunity-supplier-email-template/opportunity-supplier-email-template.module#OpportunitySupplierEmailTemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/content_library',
        loadChildren: '@app/features/system-settings/content-library/content-library.module#ContentLibraryModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/job_report_customization',
        loadChildren: '@app/features/system-settings/job-report-customisation/job-report-customisation.module#JobReportCustomisationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/opportunity-views',
        loadChildren: '@app/features/system-settings/opportunity-views/opportunity-views.module#OpportunityViewsModule'
    },
    {
        path: 'suppliers/listAutomatedInvoices',
        loadChildren: '@app/features/ai-document-parsing/ai-document-parsing.module#AiDocumentParsingModule',
        canActivate: [AuthGuard]
        //component: AiDocumentParsingComponent
    },
    {
        path: 'company_settings/user',
        loadChildren: '@app/features/company-settings/users/users.module#UsersModule',
        canActivate: [AuthGuard]
    },

    {
        path: 'system_settings/job-views',
        loadChildren: '@app/features/system-settings/job-views/job-views.module#JobViewsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/job-teams',
        loadChildren: '@app/features/system-settings/job-teams/job-teams.module#JobTeamsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/proposal_portal',
        loadChildren: '@app/features/system-settings/proposal-portal/proposal-portal.module#ProposalPortalModule',
    },
    {
        path: 'system_settings/jobs_pipelines',
        loadChildren: '@app/features/system-settings/set-up-pipelines/set-up-pipelines.module#SetUpPipelinesModule',
        canActivate: [AuthGuard],
        data: {
            screen: 'job'
        }
    },
    {
        path: 'company_settings/setup_email',
        loadChildren: '@app/features/setup-email/setup-email.module#SetupEmailModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_status',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-status/vehicle-status.module#VehicleStatusModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_makes',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-makes/vehicle-makes.module#VehicleMakesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_reminder',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-reminder/vehicle-reminder.module#VehicleReminderModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management',
        loadChildren: '@app/features/fleet-management/fleet-management-dashboard/fleet-management-dashboard.module#FleetManagementDashboardModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_reminder',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-reminder/vehicle-reminder.module#VehicleReminderModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_maintenance',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-maintenance/vehicle-maintenance.module#VehicleMaintenanceModule',
        canActivate: [AuthGuard]
    },
    // {
    //     path: 'fleet/vehicle_list',
    //     loadChildren: '@app/features/system-settings/fleet-management/vehicle-list/vehicle-list.module#VehicleListModule',
    //     canActivate: [AuthGuard]
    // },
    {
        path: 'system_settings/vehicle_check_template',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-check-template/template.module#TemplateModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management/vehicle_list',
        loadChildren: '@app/features/fleet-management/add-vehicle/vehicle.module#VehicleModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_check_forms',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-check-forms/vehicle-check-forms.module#VehicleCheckFormsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/fleet/geo-fences',
        loadChildren: '@app/features/system-settings/fleet-management/geo-fences/geo-fences.module.ts#GeoFencesModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management/reminder',
        loadChildren: '@app/features/fleet-management/dashboard-reminder/dashboard-reminder.module.ts#DashboardReminderModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management/maintenance',
        loadChildren: '@app/features/fleet-management/dashboard-maintenance/dashboard-maintenance.module.ts#DashboardMaintenanceModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management/live_map',
        loadChildren: '@app/features/fleet-management/fleet-live-map/fleet-live-map.module.ts#FleetLiveMapModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'fleet_management/trips',
        loadChildren: '@app/features/fleet-management/dashboard-trips/dashboard-trips.module.ts#DashboardTripsModule',
        canActivate: [AuthGuard]
    },
    // {
    //     path: 'system_settings/vehicle_check_template_config',
    //     loadChildren: '@app/features/system-settings/fleet-management/assign-vehicle-check-template/assign-vehicle-check-template.module#AssignVehicleCheckTemplateModule',
    //     canActivate: [AuthGuard]
    // },
    {
        path: 'system_settings/vehicle_display_tracker',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-display-tracker/vehicle-display-tracker.module.ts#VehicleDisplayTrackerModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/vehicle_out_of_hours',
        loadChildren: '@app/features/system-settings/fleet-management/vehicle-out-of-hours/vehicle-out-of-hours.module.ts#VehicleOutOfHoursModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/departure_notification',
        loadChildren: '@app/features/system-settings/fleet-management/departure-notification/departure-notification.module.ts#DepartureNotificationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/late_arrival_notification',
        loadChildren: '@app/features/system-settings/fleet-management/late-arrival-notification/late-arrival-notification.module.ts#LateArrivalNotificationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/custom_forms',
        loadChildren: '@app/features/system-settings/custom-form/custom-form.module#CustomFormModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/job_stages',
        loadChildren: '@app/features/system-settings/set-up-stages/set-up-stages.module#SetUpStagesModule',
        canActivate: [AuthGuard],
        data: {
            screen: 'job'
        }
    },
    {
        path: 'settings/jobs/arrive_questions',
        loadChildren: '@app/features/system-settings/job-arrive-leave-form/job-arrive-leave-form.module#JobArriveLeaveFormModule',
        data: { type: 'arrive' },
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/jobs/leave_questions',
        loadChildren: '@app/features/system-settings/job-arrive-leave-form/job-arrive-leave-form.module#JobArriveLeaveFormModule',
        data: { type: 'leave' },
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/defects',
        loadChildren: '@app/features/system-settings/defects-recommendation/defects-recommendation.module.ts#DefectsRecommendationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/defects/pipelines',
        loadChildren: '@app/features/system-settings/defects-recommendation/defects-recommendation.module.ts#DefectsRecommendationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/recommendations',
        loadChildren: '@app/features/system-settings/defects-recommendation/defects-recommendation.module.ts#DefectsRecommendationModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'system_settings/type_of_labour_settings',
        loadChildren: '@app/features/system-settings/type-of-labour/type-of-labour.module.ts#TypeOfLabourModule'
    },
    {
        path: 'system_settings/shifts',
        loadChildren: '@app/features/system-settings/shift-pattern/shift-pattern.module.ts#ShiftPatternModule'
    },
     {
        path: 'company_settings/purchase_trackers',
        loadChildren: '@app/features/admin/admin.module#AdminModule'
    },
    {
        path: 'customers/labour_time_approval',
        loadChildren: '@app/features/system-settings/labour-time-approval/labour-time-approval.module.ts#LabourTimeApprovalModule'
    },
    {
        path: 'company_settings/purchasetrackers',
        loadChildren: '@app/features/admin/admin.module#AdminModule'
    },
    {
        path: 'company_settings/application',
        loadChildren: '@app/features/company-settings/api/api.module#ApiModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'company_settings',
        loadChildren: '@app/features/admin/admin.module#AdminModule'
    },
    {
        path: 'setup',
        loadChildren: '@app/features/admin/admin.module#AdminModule'
    },
    {
        path: 'settings/send_proposal_template',
        loadChildren: '@app/features/system-settings/opportunity-email-template-settings/opportunity-email-template-settings.module#OpportunityEmailTemplateSettingsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'plus',
        loadChildren: '@app/features/advertising/advertising.module#AdvertisingModule'
    },
    {
        path: 'settings/on_way_notification',
        loadChildren: '@app/features/system-settings/opportunity-email-template-settings/opportunity-email-template-settings.module#OpportunityEmailTemplateSettingsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/work_address_confirmation',
        loadChildren: '@app/features/system-settings/opportunity-email-template-settings/opportunity-email-template-settings.module#OpportunityEmailTemplateSettingsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/customer_confirmation',
        loadChildren: '@app/features/system-settings/opportunity-email-template-settings/opportunity-email-template-settings.module#OpportunityEmailTemplateSettingsModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'settings/variation-templates',
        loadChildren: '@app/features/system-settings/variations/variation-template/variation-template.module.ts#VariationTemplateModule'
    },
    {
        path: 'settings/variation-option-templates',
        loadChildren: '@app/features/system-settings/variations/variation-option/variation-option.module.ts#VariationOptionModule'
    },
    {
        path: 'aiden',
        loadChildren: '@app/features/aiden/aiden.module#AidenModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'aiden_history/:agentType',
        loadChildren: '@app/features/aiden/agent-call-history/agent-call-history.module#AgentCallHistoryModule',
        canActivate: [AuthGuard]
    },
    {
        path: 'migrated_legacy_plan',
        loadChildren: '@app/features/migrated-legacy-plan/migrated-legacy-plan.module#MigratedLegacyPlanModule',
        canActivate: [AuthGuard]
    },
    { path: '**', loadChildren: '@app/features/error/error.module#ErrorModule' },
];

export const FeaturesRouting = RouterModule.forChild(FeaturesRoutes);
